/**
 * Dates — the archive grouped by when, at a grain you choose.
 *
 * The one rule this view exists to keep: a bucket is labelled to the precision
 * the archive actually holds. A photograph the scanner could only place in the
 * nineteen-fifties belongs to a bucket called "1950s", and there is no reading
 * of this screen on which it says "1 January 1950". `capturedPrecision` is not
 * decoration — it is the difference between a record and a guess, and the label
 * is where a reader can see which one they are looking at.
 *
 * `/dates` returns counts and a cover, not photographs, so each bucket fetches
 * its own page of cards when it scrolls into view.
 */

import { h, replace, clear, keepScroll } from "../dom";
import { api } from "../api";
import { store } from "../state";
import { empty, failure, fingerprint, loading, seg, tile } from "../ui";
import { formatDate, isFuzzy, precisionLabel } from "../format";
import { openAsset } from "../lightbox";
import type { DateBucket, Precision } from "../types";
import type { RefreshOptions, View } from "./view";

type Grain = "month" | "year" | "decade";

const PER_BUCKET = 24;

/**
 * What the bucket is called.
 *
 * The server sends the raw key ("1950", "1953-08") and the coarsest precision
 * in the bucket. A decade grain is always a decade regardless of what is in it;
 * otherwise the bucket's own precision decides, and a fuzzy precision coarsens
 * the key to its decade rather than spelling out a year nobody recorded.
 */
export function bucketTitle(b: DateBucket, grain: Grain): string {
  if (!b.key || b.key === "unknown") return "Date unknown";
  if (grain === "decade" || b.precision === "decade" || b.precision === "circa") {
    return formatDate(`${b.key.slice(0, 4)}-01-01`, "decade");
  }
  const shape: Precision = b.key.length >= 7 ? "month" : "year";
  return formatDate(b.key, shape);
}

/** The inclusive ISO range a bucket covers, for handing to the shared filter. */
export function bucketRange(b: DateBucket, grain: Grain): { from: string; to: string } | null {
  if (!b.key || b.key === "unknown") return null;
  const year = Number(b.key.slice(0, 4));
  if (!Number.isFinite(year)) return null;
  if (grain === "decade" || b.precision === "decade" || b.precision === "circa") {
    const start = Math.floor(year / 10) * 10;
    return { from: `${start}-01-01`, to: `${start + 9}-12-31` };
  }
  if (b.key.length >= 7) {
    const month = Number(b.key.slice(5, 7));
    const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return {
      from: `${b.key.slice(0, 7)}-01`,
      to: `${b.key.slice(0, 7)}-${String(last).padStart(2, "0")}`,
    };
  }
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

export function mount(pane: HTMLElement): View {
  let generation = 0;
  let observer: IntersectionObserver | null = null;

  function bucketEl(b: DateBucket, grain: Grain): HTMLElement {
    const range = bucketRange(b, grain);
    const fuzzy = isFuzzy(b.precision);
    const body = h("div", { class: "grid" });
    const wrap = h(
      "div",
      { class: "bucket" },
      h(
        "div",
        { class: "bhead" },
        h("span", { class: "lab", text: bucketTitle(b, grain) }),
        h("span", {
          class: fuzzy ? "prec fuzzy" : "prec",
          title: fuzzy
            ? "Nobody recorded a finer date than this. The label says only what is known."
            : "Recorded to this precision",
          text: precisionLabel(b.precision),
        }),
        h("span", { class: "n", text: `${b.count}` }),
        range
          ? h("button", {
              class: "btn xs ghost go",
              text: "Narrow to this →",
              title: "Set the date range on the shared filter and open the grid",
              onclick: () => {
                store.setFilter({ from: range.from, to: range.to });
                store.go("cards");
              },
            })
          : null,
      ),
      body,
    );

    let loaded = false;
    const fill = async (): Promise<void> => {
      if (loaded) return;
      loaded = true;
      const mine = generation;
      replace(body, loading("photographs"));
      try {
        const slice = await api.cards(
          store.libraryId,
          range ? { ...store.filter, from: range.from, to: range.to } : store.filter,
          { limit: PER_BUCKET },
        );
        if (mine !== generation) return;
        clear(body);
        for (const a of slice.items) body.appendChild(tile(a, openAsset));
        const rest = (slice.total ?? slice.items.length) - slice.items.length;
        if (rest > 0 && range) {
          body.appendChild(
            h("button", {
              class: "tile more",
              type: "button",
              text: `+${rest} more`,
              onclick: () => {
                store.setFilter({ from: range.from, to: range.to });
                store.go("cards");
              },
            }),
          );
        }
      } catch (e) {
        if (mine !== generation) return;
        loaded = false;
        replace(body, failure(e, () => void fill()));
      }
    };

    (wrap as HTMLElement & { fill?: () => void }).fill = () => void fill();
    return wrap;
  }

  /** The buckets last drawn, so a live update that changed none of them does nothing. */
  let shownSig: string | null = null;

  async function refresh(opts: RefreshOptions = {}): Promise<void> {
    generation++;
    const mine = generation;
    const quiet = Boolean(opts.quiet && shownSig !== null);
    if (!quiet) {
      shownSig = null;
      observer?.disconnect();
      observer = null;
    }

    const grain = store.dateGrain;
    const bar = h(
      "div",
      { class: "viewbar" },
      seg<Grain>(
        "Grain",
        [
          ["month", "Month"],
          ["year", "Year"],
          ["decade", "Decade"],
        ],
        grain,
        (g) => {
          store.dateGrain = g;
          void refresh();
        },
      ),
    );

    if (!quiet) replace(pane, bar, loading("buckets"));

    let buckets: DateBucket[];
    try {
      buckets = await api.dates(store.libraryId, store.filter, grain);
    } catch (e) {
      if (mine !== generation || quiet) return;
      replace(pane, bar, failure(e, () => void refresh()));
      return;
    }
    if (mine !== generation) return;

    const sig = fingerprint([grain, buckets]);
    if (quiet && sig === shownSig) return;
    shownSig = sig;
    observer?.disconnect();
    observer = null;

    if (!buckets.length) {
      replace(
        pane,
        bar,
        empty({
          icon: "calendar",
          title: store.narrowed ? "Nothing dated matches that" : "No dates yet",
          body: [
            store.narrowed
              ? "Every bucket is empty under this narrowing. Clearing the filter will bring the rest of the archive back."
              : "Once photographs are indexed they are grouped here by when they were taken — to the day where a camera recorded it, and to the decade where somebody had to guess.",
          ],
          actions: store.narrowed
            ? [h("button", { class: "btn primary", text: "Clear the filter", onclick: () => store.clearFilter() })]
            : [],
        }),
      );
      return;
    }

    bar.appendChild(h("div", { class: "grow" }));
    bar.appendChild(
      h("span", {
        class: "stat",
        text: `${buckets.length} buckets · ${buckets.reduce((n, b) => n + b.count, 0)} photographs`,
      }),
    );

    const list = h("div", { class: "buckets" });
    const els = buckets.map((b) => bucketEl(b, grain));
    for (const el of els) list.appendChild(el);
    keepScroll(pane, () => replace(pane, bar, list));

    observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          (e.target as HTMLElement & { fill?: () => void }).fill?.();
          observer?.unobserve(e.target);
        }
      },
      { root: pane, rootMargin: "600px 0px" },
    );
    for (const el of els) observer.observe(el);
  }

  void refresh();

  return {
    refresh: (opts) => void refresh(opts),
    destroy() {
      generation++;
      observer?.disconnect();
    },
  };
}
